import React, { useState, useEffect } from 'react';
import { Table, Button, Drawer, KeyValuePairs } from '@cloudscape-design/components';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import TextFilter from '@cloudscape-design/components/text-filter';
import Pagination from '@cloudscape-design/components/pagination';
import { CustomAppLayout } from '../../layout';
import { useRouter } from '../../router';
import { useStore } from '../../store';
import Box from '@cloudscape-design/components/box';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import { useGlobalState } from '../../../context/GlobalStateContext';
import { formatPhone, storeJWT } from '../../../commons/functions';
import { useStoreAdmin } from '../../../context/useStoreAdmin';

const pageSize = 20;

export const SecurityExclusionsPage = () => {
  const storeAdmin = useStoreAdmin();
  const { idPropiedad, setIdPropiedad } = useGlobalState();
  const { idControl, setIdControl } = useGlobalState();
  const [selectedRow, setSelectedRow] = useState<any>(null);  // Stores selected row data
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [btnAuths, setBtnAuths] = useState(true);
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);
  const { JWT, setJWT } = useGlobalState();

  const {
    actions: { makeHelpPanelHandler },
  } = useStore();
  const { navigate } = useRouter();
  const [selectedItems, setSelectedItems] = useState<ExclusionItem[]>([]);
  const { setLoading } = useGlobalState();
  const [btnActionDisable, setBtnActionDisable] = useState<boolean>(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [filteringText, setFilteringText] = useState('');
  const [exclusions, setExclusions] = useState<ExclusionItem[]>([]);


  type ExclusionItem = {
    idExclusion: number;
    exclusion: string;
    startTime: string;
    endTime: string;
    weekDay: string;
    weekDayString: string;
  };
  const handleCheckboxChange = (exclusionId: string) => {
    setSelectedPolicies((prev) => {
      // If the access is already selected, remove it from the array, else add it
      if (prev.includes(exclusionId)) {
        return prev.filter(id => id !== exclusionId);
      } else {
        return [...prev, exclusionId];
      }
    });
  };
  const filteredPolicies = exclusions.filter(exclusion => {
    const searchWords = filteringText.toLowerCase().split(/\s+/).filter(Boolean);
    return searchWords.every(word =>
      exclusion.exclusion.toLowerCase().includes(word) ||
      exclusion.startTime.toLowerCase().includes(word) ||
      exclusion.endTime.toLowerCase().includes(word)
    );
  });

  const paginatedPolicies = filteredPolicies.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

  const handleSelectionChange = ({ detail }: any) => {
    setSelectedItems(detail.selectedItems);
    setBtnActionDisable(detail.selectedItems.length === 0);
    if (detail.selectedItems.length > 1) setDrawerOpen(false);
  };

  useEffect(() => {
    setBtnAuths(selectedPolicies.length == 0)
  }, [selectedPolicies]); // The effect runs every time selectedAccesses changes

  // Handle row click to fetch additional data and open the HelpPanel
  const handleRowClick = async (rowData: ExclusionItem) => {
    setSelectedRow(rowData);  // Store selected row data
    setLoading(true);
    try {
      // Simulate a fetch request to get additional data for the row
      // Example of a fetched data structure
      const exclusionDetails = {
        idExclusion: rowData.idExclusion,
        exclusion: rowData.exclusion,
        startTime: rowData.startTime,
        endTime: rowData.endTime,
      };
      setSelectedRow(exclusionDetails);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setDrawerOpen(true);
    }
  };
  useEffect(() => {
  }, [selectedRow]);
  const handleRefresh = async () => {
    try {
      setLoading(false)
      const response = await fetch('https://api2.llavemovil.com/routing/v1/default.aspx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-routing': '/users/user/login',
          'JWT': JWT,

        },
        body: JSON.stringify({
          idPropiedadAdmin: idPropiedad,
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();

      storeAdmin(data);
      setExclusions(data.admin.exclusions);

    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    handleRefresh();
  }, []);

  return (
    <CustomAppLayout
      toolsOpen={drawerOpen}
      onToolsChange={({ detail }) => setDrawerOpen(detail.open)}
      tools={


        <Drawer header="">
          <div>
            {selectedRow ? (
              <>
                <h3>{formatPhone(selectedRow.movil)}</h3>
                <h3>{selectedRow.displayName}</h3>
                <KeyValuePairs
                  columns={2}
                  items={[
                    {
                      label: "Email",
                      value: selectedRow.email
                    },
                    {
                      label: "Unidad",
                      value: selectedRow.unidad
                    }
                  ]}
                />

                <div style={{ height: '20px' }}></div>
                <SpaceBetween size='xs' direction="vertical">
                </SpaceBetween>
              </>
            ) : (
              <p>No hay datos seleccionados.</p>
            )}
          </div>
          <div style={{ height: '20px' }}></div>
          <SpaceBetween size='xs' alignItems="end" direction="horizontal">
            <Button variant="primary" disabled={btnAuths}>Autorizar</Button>
            <Button variant="normal" disabled={btnAuths}>Denegar</Button>
          </SpaceBetween>
        </Drawer>
      }

      contentType="form"
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Inicio', href: '#home' },
            { text: 'Seguridad', href: '#security' },
            { text: 'Exclusiones', href: '#create-transcription-job' },
          ]}
          expandAriaLabel="Show path"
          ariaLabel="Breadcrumbs"
        />
      }
      content={
        <>
          <Table
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
              `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            onSelectionChange={handleSelectionChange}
            selectedItems={selectedItems}
            onRowClick={({ detail }) => handleRowClick(detail.item)}
            ariaLabels={{
              selectionGroupLabel: "Items selection",
              allItemsSelectionLabel: () => "select all",
              itemSelectionLabel: ({ selectedItems }, item) => item.idExclusion.toString(),
            }}
            columnDefinitions={[
              {
                id: "Exclusion",
                header: "Política",
                cell: (item) => <Link href="#">{item.exclusion}</Link>,
                sortingField: "Exclusion",
                isRowHeader: true,
              },
              {
                id: "startTime",
                header: "Inicio",
                cell: (item) => item.startTime,
                sortingField: "startTime",
              },
              {
                id: "endTime",
                header: "Término",
                cell: (item) => item.endTime,
              },
              {
                id: "weekDay",
                header: "Días",
                cell: (item) => item.weekDayString,
              },
            ]}
            items={paginatedPolicies}
            selectionType="multi"
            trackBy="idExclusion"
            empty={
              <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                <SpaceBetween size="m">
                  <b>No tiene exclusiones registradas</b>
                </SpaceBetween>
              </Box>
            }
            filter={
              <TextFilter
                filteringText={filteringText}
                onChange={({ detail }) => {
                  setFilteringText(detail.filteringText);
                  setCurrentPageIndex(1);
                }}
              />
            }
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                pagesCount={Math.ceil(filteredPolicies.length / pageSize)}
                onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              />
            }

            preferences={
              <CollectionPreferences
                title="Preferencias"
                confirmLabel="Confirmar"
                cancelLabel="Cancelar"
                preferences={{
                  pageSize: 10,
                  contentDisplay: [
                    { id: "movil", visible: true },
                    { id: "displayName", visible: true },
                    { id: "accesos", visible: true },
                    { id: "unidad", visible: true }
                  ]
                }}
                pageSizePreference={{
                  title: "Paginación",
                  options: [
                    { value: 10, label: "10 items" },
                    { value: 20, label: "20 items" }
                  ]
                }}
                wrapLinesPreference={{}}
                stripedRowsPreference={{}}
                contentDensityPreference={{

                }}
                contentDisplayPreference={{
                  options: [
                    {
                      id: "movil",
                      label: "Movil",
                      alwaysVisible: true
                    },
                    { id: "displayName", label: "Nombre" },
                    { id: "unidad", label: "Unidad" },
                    { id: "accesos", label: "Accesos" }
                  ]
                }}
                stickyColumnsPreference={{
                  firstColumns: {
                    title: "Fijar primera columna(s)",
                    description:
                      "Mantener la primera columna(s) visibles mientras se hace scroll horizonal en el contenido de la tabla.",
                    options: [
                      { label: "Ninguno", value: 0 },
                      { label: "Primera columna", value: 1 },
                      { label: "Primeras dos columna", value: 2 }
                    ]
                  },
                  lastColumns: {
                    title: "Fijar última columna",
                    description:
                      "Keep the last column visible while horizontally scrolling the table content.",
                    options: [
                      { label: "Ninguno", value: 0 },
                      { label: "Última columna", value: 1 }
                    ]
                  }
                }}
              />
            }
            header={
              <Header
                counter={
                  selectedItems.length
                    ? "(" + selectedItems.length + "/" + pageSize + ")"
                    : ""
                }
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <ButtonDropdown
                      items={[
                        {
                          text: "Autorizar todos los accesos",
                          id: "auth",
                          disabled: false
                        },
                        {
                          text: "Denegar todos los accesos",
                          id: "deny",
                          disabled: false
                        },
                      ]}
                      disabled={btnActionDisable}
                    >
                      Acciones
                    </ButtonDropdown>
                    <Button variant="primary" href='#security/Exclusions.add'>
                      Agregar Exclusión
                    </Button>
                  </SpaceBetween>
                }
              >
              </Header>
            }
          />

        </>
      }
    />
  );
};
